const normalizeDateInput = (input) => {
    if (!input && input !== 0) {
        return null;
    }

    if (input instanceof Date) {
        return Number.isNaN(input.getTime()) ? null : input;
    }

    if (typeof input === 'number' && Number.isFinite(input)) {
        const fromNumber = new Date(input);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }

    const raw = `${input}`.trim();
    if (!raw) {
        return null;
    }

    // If the string already specifies a timezone (UTC Z or offset), trust it
    if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(raw)) {
        const zonedDate = new Date(raw);
        return Number.isNaN(zonedDate.getTime()) ? null : zonedDate;
    }

    // Handle common timestamp formats without timezone by assuming UTC
    const isoLikeMatch = raw.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?)$/);
    if (isoLikeMatch) {
        const [, datePart, timePart = '00:00:00'] = isoLikeMatch;
        const normalizedTime = timePart.length === 5 ? `${timePart}:00` : timePart;
        const normalized = `${datePart}T${normalizedTime}Z`;
        const utcDate = new Date(normalized);
        if (!Number.isNaN(utcDate.getTime())) {
            return utcDate;
        }
    }

    // Fallback to native parsing
    const fallbackDate = new Date(raw);
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
};

export const parseUtcDate = (value) => normalizeDateInput(value);

export const getUtcTimestamp = (value) => {
    const parsedDate = parseUtcDate(value);
    return parsedDate ? parsedDate.getTime() : null;
};

export const formatToLocalDateTime = (value, options = {}) => {
    const {
        fallback = 'Date unavailable',
        includeSeconds = true,
        locale,
        hour12 = true,
        dateStyle,
        timeStyle,
        timeZoneName,
        formatOverrides,
    } = options;

    const parsedDate = parseUtcDate(value);
    if (!parsedDate) {
        return fallback;
    }

    const hasExplicitTimeZoneName = Object.prototype.hasOwnProperty.call(options, 'timeZoneName');
    const resolvedTimeZoneName = hasExplicitTimeZoneName
        ? timeZoneName || undefined
        : 'short';

    const fallbackOptions = {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        ...(includeSeconds ? { second: '2-digit' } : {}),
        hour12,
        ...(resolvedTimeZoneName ? { timeZoneName: resolvedTimeZoneName } : {}),
    };

    const baseOptionsCandidate = formatOverrides && typeof formatOverrides === 'object'
        ? formatOverrides
        : (dateStyle || timeStyle)
            ? {
                ...(dateStyle ? { dateStyle } : {}),
                ...(timeStyle ? { timeStyle } : {}),
                ...(resolvedTimeZoneName ? { timeZoneName: resolvedTimeZoneName } : {}),
            }
            : fallbackOptions;

    const sanitizedOptions = Object.fromEntries(
        Object.entries(baseOptionsCandidate).filter(([, optionValue]) => optionValue !== undefined)
    );

    try {
        return new Intl.DateTimeFormat(locale, sanitizedOptions).format(parsedDate);
    } catch (datetimeFormatError) {
        if ((dateStyle || timeStyle || (formatOverrides && typeof formatOverrides === 'object'))) {
            try {
                return new Intl.DateTimeFormat(locale, fallbackOptions).format(parsedDate);
            } catch {
                // continue to ultimate fallback below
            }
        }

        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[formatToLocalDateTime] Falling back to locale string due to Intl.DateTimeFormat error:', datetimeFormatError);
        }

        return locale
            ? parsedDate.toLocaleString(locale)
            : parsedDate.toLocaleString();
    }
};
